import { FC, useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Badge, Table, Select, TextInput, Spinner } from '../../lib';
import { CreateReportModal } from '../../lib/components/StewardReports/CreateReportModal';
import stewardReportServices from '../../services/stewardReportServices';
import { StewardReportHeader } from '../../services/dtos';

type DateRangePreset = 'last90' | 'thisWeek' | 'lastWeek' | 'custom';

export const ReportsListPage: FC = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState<StewardReportHeader[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Filters
  const [datePreset, setDatePreset] = useState<DateRangePreset>('last90');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showCreateModal, setShowCreateModal] = useState(false);

  const svc = new stewardReportServices();

  // Calculate date ranges for presets
  const getDateRange = (preset: DateRangePreset): { start: string; end: string } => {
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    
    switch (preset) {
      case 'last90':
        const last90 = new Date(today);
        last90.setDate(last90.getDate() - 90);
        return { start: last90.toISOString().split('T')[0], end: todayStr };
      
      case 'thisWeek':
        const thisWeekStart = new Date(today);
        thisWeekStart.setDate(today.getDate() - today.getDay()); // Start of week (Sunday)
        return { start: thisWeekStart.toISOString().split('T')[0], end: todayStr };
      
      case 'lastWeek':
        const lastWeekEnd = new Date(today);
        lastWeekEnd.setDate(today.getDate() - today.getDay() - 1); // End of last week
        const lastWeekStart = new Date(lastWeekEnd);
        lastWeekStart.setDate(lastWeekEnd.getDate() - 6); // Start of last week
        return {
          start: lastWeekStart.toISOString().split('T')[0],
          end: lastWeekEnd.toISOString().split('T')[0],
        };
      
      case 'custom':
        return { start: startDate, end: endDate };
      
      default:
        return { start: '', end: '' };
    }
  };

  // Fetch reports
  const fetchReports = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const range = getDateRange(datePreset);
      const headers = await svc.getHeaders(range.start || undefined, range.end || undefined);
      setReports(headers);
    } catch (err: any) {
      console.error('Error fetching reports:', err);
      if (err.type === 'RefreshTokenException' || err.responseStatus?.errorCode === '403' || err.responseStatus?.errorCode === '401') {
        navigate('/login');
      } else {
        setError('Failed to load reports. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [datePreset, startDate, endDate]);

  // Filter and sort reports client-side
  const filteredReports = useMemo(() => {
    let filtered = [...reports];

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((r) => r.status === statusFilter);
    }

    // Search filter - case insensitive partial match across multiple fields
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      filtered = filtered.filter((r) => {
        const location = (r.reportToLocation || '').toLowerCase();
        const reportTo = (r.reportToPerson || '').toLowerCase();
        const site = (r.site || '').toLowerCase();
        const employer = (r.employer || '').toLowerCase();
        const jobId = (r.jobId || '').toLowerCase();
        
        return (
          location.includes(searchLower) ||
          reportTo.includes(searchLower) ||
          site.includes(searchLower) ||
          employer.includes(searchLower) ||
          jobId.includes(searchLower)
        );
      });
    }

    // Sort by end date descending
    filtered.sort((a, b) => {
      const dateA = a.reportEndDate ? new Date(a.reportEndDate).getTime() : 0;
      const dateB = b.reportEndDate ? new Date(b.reportEndDate).getTime() : 0;
      return dateB - dateA; // Descending order
    });

    return filtered;
  }, [reports, statusFilter, searchTerm]);

  // Format dates in short m/d format
  const formatShortDate = (dateStr?: string): string => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  // Format date range in short format (m/d - m/d)
  const formatShortDateRange = (begin?: string, end?: string): string => {
    if (!begin || !end) return 'N/A';
    return `${formatShortDate(begin)} - ${formatShortDate(end)}`;
  };

  const handleRowClick = (headerId: number) => {
    navigate(`/reports/${headerId}`);
  };

  const handleCreateNew = () => {
    setShowCreateModal(true);
  };

  const handleCreateSuccess = (headerId: number) => {
    setShowCreateModal(false);
    navigate(`/reports/${headerId}`);
  };

  return (
    <div className="container mx-auto p-4 max-w-7xl">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold dark:text-white">Steward Reports</h1>
        <Button 
          onClick={handleCreateNew}
          color="info"
          pill
          size="sm"
        >
          <span className="flex items-center gap-2">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Create New Report
          </span>
        </Button>
      </div>

      {/* Filters and Search */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {/* Search Box */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Search
            </label>
            <div className="w-full">
              <TextInput
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by job ID, employer, site, location, or report-to..."
              />
            </div>
          </div>
          {/* Date Range Preset */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Date Range
            </label>
            <Select
              value={datePreset}
              onChange={(e) => {
                setDatePreset(e.target.value as DateRangePreset);
                if (e.target.value !== 'custom') {
                  setStartDate('');
                  setEndDate('');
                }
              }}
            >
              <option value="last90">Last 90 Days</option>
              <option value="thisWeek">This Week</option>
              <option value="lastWeek">Last Week</option>
              <option value="custom">Custom Range</option>
            </Select>
          </div>

          {/* Custom Date Range */}
          {datePreset === 'custom' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Start Date
                </label>
                <TextInput
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  End Date
                </label>
                <TextInput
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </>
          )}

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Status
            </label>
            <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All</option>
              <option value="Draft">Draft</option>
              <option value="Submitted">Submitted</option>
            </Select>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Reports Table */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Spinner size="xl" />
        </div>
      ) : filteredReports.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            {reports.length === 0
              ? 'No reports found. Create your first report to get started.'
              : 'No reports match your filters.'}
          </p>
          {reports.length === 0 && (
            <Button onClick={handleCreateNew}>Create New Report</Button>
          )}
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <Table>
            <Table.Head>
              <Table.HeadCell>Job ID</Table.HeadCell>
              <Table.HeadCell>Employer</Table.HeadCell>
              <Table.HeadCell>Site</Table.HeadCell>
              <Table.HeadCell>Dates</Table.HeadCell>
              <Table.HeadCell>Status</Table.HeadCell>
              <Table.HeadCell>Location / Report-to</Table.HeadCell>
              <Table.HeadCell>Actions</Table.HeadCell>
            </Table.Head>
            <Table.Body className="divide-y">
              {filteredReports.map((report) => (
                <Table.Row
                  key={report.id}
                  className="bg-white dark:border-gray-700 dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                  onClick={() => handleRowClick(report.id)}
                >
                  <Table.Cell className="whitespace-nowrap font-medium text-gray-900 dark:text-white">
                    {report.jobId}
                  </Table.Cell>
                  <Table.Cell>{report.employer || 'N/A'}</Table.Cell>
                  <Table.Cell>{report.site || 'N/A'}</Table.Cell>
                  <Table.Cell className="whitespace-nowrap">
                    {formatShortDateRange(report.reportBeginDate, report.reportEndDate)}
                  </Table.Cell>
                  <Table.Cell>
                    <Badge
                      color={report.status === 'Submitted' ? 'success' : 'warning'}
                    >
                      {report.status}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <div>
                      <div className="font-medium">{report.reportToLocation}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {report.reportToPerson}
                      </div>
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <Button
                      size="sm"
                      pill
                      color="info"
                      outline
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRowClick(report.id);
                      }}
                    >
                      <span className="flex items-center gap-1.5">
                        {report.status === 'Submitted' ? (
                          <>
                            <svg
                              className="w-3.5 h-3.5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                              />
                            </svg>
                            View
                          </>
                        ) : (
                          <>
                            <svg
                              className="w-3.5 h-3.5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                              />
                            </svg>
                            Open
                          </>
                        )}
                      </span>
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      )}

      {/* Results count */}
      {!loading && filteredReports.length > 0 && (
        <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
          Showing {filteredReports.length} of {reports.length} report{reports.length !== 1 ? 's' : ''}
        </div>
      )}

      {/* Create Report Modal */}
      <CreateReportModal
        show={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleCreateSuccess}
      />
    </div>
  );
};

